/**
 * Unwraps a standard { status, message, results/data/next } API response,
 * throwing a consistent error when the request didn't succeed.
 *
 * Extracted from the identical hand-rolled logic repeated across
 * src/api/services/* (~87 files): check response.data.status !== "OK",
 * pull the payload out depending on the endpoint, and throw a fallback
 * Error otherwise. New services should use this directly; existing ones
 * can be migrated opportunistically.
 *
 * @param {object} response - The axios response object.
 * @param {object} options
 * @param {"results"|"resultsRaw"|"data"|"next"|"responseData"} [options.extract="results"]
 *   Which shape to pull the payload from on success:
 *   - "results": response.data.results[0]?.properties (e.g. getBankNames, sendBankDetails)
 *   - "resultsRaw": response.data.results as-is (e.g. getUserStatus, getAddressDropdown — the most common shape)
 *   - "data": response.data.data (e.g. companyDetails)
 *   - "next": response.data.next (e.g. resendEmailOtp, sendEmailOtp)
 * @param {string} options.fallbackMessage - Shown when the server doesn't
 *   provide its own message on failure.
 * @returns {*} The unwrapped payload.
 */

export const unwrapApiResponse = (
  response,
  { extract = "results", fallbackMessage, defaultDataType = {} }
) => {
  if (response?.data?.status !== "OK") {
    throw new Error(response?.data?.message || fallbackMessage);
  }

  if (extract === "results" && response.data?.results?.length > 0) {
    return response.data.results[0]?.properties ?? defaultDataType;
  }

  if (extract === "resultsRaw" && response.data?.results) {
    return response.data.results ?? defaultDataType;
  }

  if (extract === "responseData" && response.data) {
    return response.data ?? defaultDataType;
  }

  if (extract === "data" && response.data?.data) return response.data.data;

  if (extract === "next" && response.data?.next) return response.data.next;

  return defaultDataType;
};
